import { useContext, useRef } from 'react';
import dynamic from 'next/dynamic';
import { getAlgoliaFacets } from '@algolia/autocomplete-js';
import algoliasearch from 'algoliasearch/lite';
import Autocomplete from '../Autocomplete/Autocomplete';
import {
  TagsInputWrapper,
  TagsWrapper,
  LocationWrapper,
  LocationInput,
  TextEditorWrapper,
} from './PostAJobFormJobDetails.styles';
import {
  Container,
  Title,
  InputsContainer,
  ButtonWrapper,
  StyledButton,
  StyledButtonsWrapper,
  StyledCompensationWrapper,
  StyledUl,
  Input,
} from '../PostAJobFormComponent/PostAJobFormComponent.styles';
import { CrossIcon } from '@/icons';
import Button from '../Button/Button';
import { DeviceContext, PostAJobContext } from '@/utils/context';
import { COMPANY, CHECKOUT } from '@/utils/constants';

import { Item } from '../SearchBarComponent/SearchBarComponent.styles';
import { maxFacetHits } from '@/utils/algoliaVariables';
import { formats, modules, mobileModules } from '@/utils/textarea';
import 'react-quill/dist/quill.snow.css';

const ReactQuill = dynamic(() => import(`react-quill`), { ssr: false });

interface Location {
  geometry: {
    location: {
      lng: () => void;
      lat: () => void;
    };
  };
  address_components: {
    short_name?: string;
    long_name?: string;
  }[];
}

const searchClient = algoliasearch(
  process.env.ALGOLIA_APPLICATION_ID || ``,
  process.env.ALGOLIA_API_KEY || ``,
);

const PostAJobFormJobDetails = (props: any) => {
  const { formState, setFormState, errorsState, setErrorsState } =
    props.postAJobProps;
  const { setIsJobReady, setCurrentStep } = useContext(PostAJobContext);
  const isMobile = useContext(DeviceContext);
  const changeHandler = (e: any) => {
    setFormState((prevState: any) => {
      return {
        ...prevState,
        [e.target.name]: e.target.value,
      };
    });
  };

  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const checkboxChangeHandler = (e: any) => {
    inputRef.current.value = ``;
    setFormState((prevState: any) => {
      return {
        ...prevState,
        locationCountry: `Anywhere`,
        locationCity: `Anywhere`,
        latitude: 0,
        longitude: 0,
        [e.target.name]: !prevState[e.target.name],
      };
    });
  };
  const numbersChangeHandler = (e: any) => {
    setFormState((prevState: any) => {
      return {
        ...prevState,
        [e.target.name]: Number(e.target.value),
      };
    });
  };
  const removeTags = (tagToRemove: string) => {
    setFormState((prevState: any) => {
      return {
        ...prevState,
        tags: [...prevState.tags.filter((tag: string) => tag !== tagToRemove)],
      };
    });
  };
  const addTags = (value: any) => {
    if (value !== `` && !formState.tags.includes(value)) {
      setFormState((prevState: any) => {
        return {
          ...prevState,
          tags: [...prevState.tags, value],
        };
      });
    }
  };
  const jobButtonClickHandler = () => {
    if (
      formState.title.trim() &&
      formState.tags.length &&
      formState.applyUrl &&
      formState.description.replace(/<p><br><\/p>/g, ``)
    ) {
      if (formState.remote) {
        setCurrentStep(CHECKOUT);
        setIsJobReady(true);
      } else if (
        formState.locationCountry &&
        formState.locationCity &&
        formState.longitude &&
        formState.latitude
      ) {
        setCurrentStep(CHECKOUT);
        setIsJobReady(true);
      }
    } else {
      setIsJobReady(false);
      const errors: any = {};
      if (!formState.title.trim()) {
        errors.title = true;
      }
      if (!formState.tags.length) {
        errors.tags = true;
      }
      if (
        !formState.remote &&
        (!formState.locationCountry ||
          !formState.locationCity ||
          !formState.longitude ||
          !formState.latitude)
      ) {
        errors.location = true;
      }
      if (!formState.description.replace(/<p><br><\/p>/g, ``)) {
        errors.description = true;
      }
      if (!formState.applyUrl) {
        errors.applyUrl = true;
      }
      setErrorsState((prevState: any) => {
        return {
          ...prevState,
          ...errors,
        };
      });
    }
  };
  const locationHandler = (place: Location) => {
    if (place && place.geometry && place.geometry.location) {
      setFormState((prevState: any) => {
        return {
          ...prevState,
          locationCity: place.address_components[0].long_name,
          locationCountry:
            place.address_components[place.address_components.length - 1]
              .short_name,
          longitude: place.geometry.location.lng(),
          latitude: place.geometry.location.lat(),
        };
      });
    }
  };
  const setFormStateDescription = (value: string) => {
    setFormState((prevState: any) => {
      return {
        ...prevState,
        description: value,
      };
    });
  };
  const setFormStateInstructions = (value: string) => {
    setFormState((prevState: any) => {
      return {
        ...prevState,
        instructions: value,
      };
    });
  };

  const onSubmitHandler = ({ state }: any) => {
    if (!formState.tags.includes(state.query.trim().toLowerCase())) {
      addTags(state.query.trim().toLowerCase());
    }
  };

  const onItemSelectHandler = (item: string, refresh: () => void) => {
    if (!formState.tags.includes(item)) {
      setFormState((prevState: any) => {
        return {
          ...prevState,
          tags: [...prevState.tags, item],
        };
      });
      refresh();
    }
  };

  const getSourcesHandler = ({ query, refresh }: any) => [
    {
      sourceId: `_tags`,
      getItems() {
        return getAlgoliaFacets({
          searchClient,
          queries: [
            {
              indexName: `verified-jobs`,
              facet: `_tags`,
              params: {
                facetQuery: query,
                maxFacetHits,
              },
            },
          ],
        });
      },
      templates: {
        item({ item }: any) {
          return <Item>{item.label}</Item>;
        },
      },
      onSelect({ item }: any) {
        onItemSelectHandler(item.label.toLowerCase(), refresh);
      },
    },
  ];

  return (
    <Container>
      <Title>Job Details</Title>
      <InputsContainer>
        <label>
          Job Title <span>(required)</span>
        </label>
        <Input
          type="text"
          name="title"
          value={formState.title}
          onChange={changeHandler}
          placeholder="Job Title"
          isEmpty={errorsState.title && !formState.title}
        />
        <label>
          Tags <span>(required)</span>
        </label>
        <TagsWrapper isEmpty={errorsState.tags && !formState.tags.length}>
          <StyledUl>
            {formState.tags.map((tag: string) => (
              <li key={tag}>
                <span>{tag}</span>
                <button onClick={() => removeTags(tag)}>
                  <CrossIcon />
                </button>
              </li>
            ))}
          </StyledUl>
          <TagsInputWrapper>
            <Autocomplete
              placeholder="e.g. javascript, vuejs, react"
              openOnFocus={true}
              onSubmit={onSubmitHandler}
              detachedMediaQuery={false}
              getSources={getSourcesHandler}
            />
          </TagsInputWrapper>
        </TagsWrapper>
        <label>
          Job Description <span>(required)</span>
        </label>
        <TextEditorWrapper
          isEmpty={
            errorsState.description &&
            !formState.description.replace(/<p><br><\/p>/g, ``)
          }
        >
          <ReactQuill
            value={formState.description}
            onChange={(e) => setFormStateDescription(e)}
            placeholder="Add description here"
            modules={isMobile ? mobileModules : modules}
            formats={formats}
          />
        </TextEditorWrapper>
        <label>
          How to apply instructions <span>(optional)</span>
        </label>
        <TextEditorWrapper>
          <ReactQuill
            value={formState.instructions}
            onChange={(e) => setFormStateInstructions(e)}
            placeholder="Add description here"
            modules={isMobile ? mobileModules : modules}
            formats={formats}
          />
        </TextEditorWrapper>
        <StyledCompensationWrapper>
          <div>
            <label>
              Min annual compansation <span>(optional)</span>
            </label>
            <Input
              type="text"
              name="minCompensation"
              value={formState.minCompensation}
              onChange={numbersChangeHandler}
              placeholder="Enter amount"
            />
          </div>
          <div>
            <label>
              Max annual compansation <span>(optional)</span>
            </label>
            <Input
              type="text"
              name="maxCompensation"
              value={formState.maxCompensation}
              onChange={numbersChangeHandler}
              placeholder="Enter amount"
            />
          </div>
        </StyledCompensationWrapper>
        <label>
          Location <span>(required)</span>
        </label>
        <LocationWrapper
          isEmpty={
            errorsState.location &&
            !formState.locationCountry &&
            !formState.remote
          }
        >
          <LocationInput
            apiKey={process.env.GOOGLE_API_KEY}
            onPlaceSelected={locationHandler}
            placeholder="Enter location"
            ref={inputRef}
            disabled={formState.remote}
          />
        </LocationWrapper>
        <input
          type="checkbox"
          name="remote"
          checked={formState.remote}
          onChange={checkboxChangeHandler}
          id="remote"
        />
        <label htmlFor="remote">Remote</label>
        <label>
          URL <span>(required)</span>
        </label>
        <Input
          type="text"
          name="applyUrl"
          value={formState.applyUrl}
          onChange={changeHandler}
          placeholder="http://"
          isEmpty={errorsState.applyUrl && !formState.applyUrl}
        />
      </InputsContainer>
      <StyledButtonsWrapper>
        <StyledButton onClick={() => setCurrentStep(COMPANY)}>
          Go to Company Details
        </StyledButton>
        <ButtonWrapper>
          <Button clickHandler={jobButtonClickHandler}>Go to Preview</Button>
        </ButtonWrapper>
      </StyledButtonsWrapper>
    </Container>
  );
};

export default PostAJobFormJobDetails;
